import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useRef,
} from 'react';
import {
  useUser as useClerkUser,
  useAuth as useClerkAuth,
} from '@clerk/clerk-react';
import { useMockUser } from '../mocks/MockClerkProvider';
import logger from '../utils/logger';
import { getOrCreateDeviceId, removeEncryptedToken } from '../utils/crypto';
import AuthService, {
  User,
  AuthError,
  RegisterWithEmailVerificationRequest,
  RegisterWithEmailVerificationResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  MFAEnableRequest,
  MFAEnableResponse,
  MFADisableRequest,
  MFADisableResponse,
  MFAVerifyRequest,
  MFAVerifyResponse,
  MFAStatusResponse,
  MFAGenerateBackupCodesRequest,
  MFAGenerateBackupCodesResponse,
} from '../services/authService';
import { LEGACY_TENANT_CONTEXT_KEYS } from '../services/auth/constants';
import { generateSecureSessionId } from '../utils/crypto';
import { AccountDataService } from '../services/accountDataService';
import { InitialDataService } from '../services/initialDataService';
import {
  PreviewAuthService,
  PreviewUser,
} from '../services/previewAuthService';
import {
  // Issue #1567: getOrganizations, getUserRoles, saveOrganizations, saveUserRoles は
  // restoreOrganizationInfo() 削除に伴い不要
  clearOrganizationCache,
  // Issue #1246: ユーザースコープストレージ
  migrateToUserScopedStorage,
  clearGlobalOrganizationCache,
  // WT2引き継ぎ (Issue #1285): キーマイグレーションロック
  acquireMigrationLock,
  releaseMigrationLock,
  isMigrationCompleted,
  markMigrationCompleted,
  KEY_MIGRATION_MAP,
} from '../utils/storageUtils';
import {
  FEATURE_PLAN_MAP,
  PLAN_HIERARCHY,
} from '../components/plan/FeatureGate';
import { useAuthStore } from '../store/useAuthStore';
import { identifyUser, trackLoginSuccess, trackLogout } from '@/lib/analytics';

/**
 * Issue #1417: プランに基づいて利用可能な機能リストを取得
 * @param plan - 'light' | 'standard' | 'premium'
 * @returns 利用可能な機能名の配列
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getFeaturesForPlan(plan: string): string[] {
  const planLevel = PLAN_HIERARCHY[plan] ?? PLAN_HIERARCHY['light'];
  return Object.entries(FEATURE_PLAN_MAP)
    .filter(([, requiredPlan]) => planLevel >= PLAN_HIERARCHY[requiredPlan])
    .map(([feature]) => feature);
}

// Issue #1280: 型付きエラー定義（文字列ベース検出の脆弱性解消）
export type AuthErrorType =
  | 'SESSION_EXPIRED' // セッション期限切れ
  | 'AUTH_ERROR' // 認証エラー
  | 'NETWORK_ERROR' // ネットワークエラー
  | 'UNKNOWN'; // その他

export interface AuthStateError {
  type: AuthErrorType;
  message: string;
}

// 認証状態の型定義
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  isInitializing: boolean;
  error: AuthStateError | null; // Issue #1280: 型付きエラー
  sessionId: string | null;
}

// 認証フックの戻り値型定義
export interface UseAuth extends AuthState {
  logout: () => Promise<void>;
  checkPermission: (action: string) => boolean;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  registerWithEmailVerification: (
    userData: RegisterWithEmailVerificationRequest
  ) => Promise<RegisterWithEmailVerificationResponse>;
  verifyEmail: (request: VerifyEmailRequest) => Promise<VerifyEmailResponse>;
  resendVerificationEmail: (userId: string) => Promise<void>;
  // MFA関連メソッド
  enableMFA: (request: MFAEnableRequest) => Promise<MFAEnableResponse>;
  disableMFA: (request: MFADisableRequest) => Promise<MFADisableResponse>;
  verifyMFA: (request: MFAVerifyRequest) => Promise<MFAVerifyResponse>;
  getMFAStatus: (userId: string) => Promise<MFAStatusResponse>;
  generateNewBackupCodes: (
    request: MFAGenerateBackupCodesRequest
  ) => Promise<MFAGenerateBackupCodesResponse>;
  // Preview環境専用メソッド（Issue #485）
  setPreviewUser: (user: PreviewUser) => void;
  clearPreviewUser: () => void;
}

// 認証コンテキスト
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<UseAuth | null>(null);

// 認証フック
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): UseAuth => {
  // Issue #480: Clerk認証状態を取得
  // 開発モード（DEV_LOGIN有効）ではモックを使用、それ以外は実際のClerk SDKを使用
  const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;
  const mode = import.meta.env.MODE;
  const isDevMode = mode === 'development' && enableDevLogin === 'true';

  // 本番環境では@clerk/clerk-reactのuseUserを使用
  const clerkData = useClerkUser();
  const clerkAuthData = useClerkAuth();
  // 開発環境（DEV_LOGIN有効）ではモックを使用
  const mockClerkData = useMockUser();

  // 環境に応じて適切なデータソースを選択
  const {
    isLoaded: clerkIsLoaded,
    isSignedIn: clerkIsSignedIn,
    user: clerkUser,
  } = isDevMode ? mockClerkData : clerkData;

  // Clerk getToken function (production only)
  const clerkGetToken = clerkAuthData.getToken;

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [error, setError] = useState<AuthStateError | null>(null); // Issue #1280: 型付きエラー
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Token更新用のタイマーRef
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 重複実行防止用のフラグ
  const isCheckingRef = useRef<boolean>(false);
  // PostHog: sessionStorageでページリロードとログインを区別
  const POSTHOG_LOGIN_TRACKED_KEY = 'posthog_login_tracked';

  // 初期化時に認証状態を確認
  useEffect(() => {
    checkAuthState();
    return () => {
      // クリーンアップ: タイマーをクリア
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回のみ実行したいため、意図的に依存配列は空

  // CRITICAL FIX (Issue #633): Clerk SDKロード完了を監視して認証状態を再チェック
  useEffect(() => {
    if (clerkIsLoaded) {
      checkAuthState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkIsLoaded]); // clerkIsLoaded が変化したら実行

  // CRITICAL FIX (Issue #674): Clerk認証状態の変化を監視
  // 新規ユーザーがメール認証完了後にログイン状態になるケースを検知
  useEffect(() => {
    if (clerkIsLoaded && clerkIsSignedIn && clerkUser && !isAuthenticated) {
      checkAuthState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkIsLoaded, clerkIsSignedIn, clerkUser]); // Clerk認証状態が変化したら実行

  /**
   * 内部ログアウト処理
   */
  const handleLogoutInternal = useCallback(async () => {
    setIsAuthenticated(false);
    setUser(null);
    setSessionId(null);
    setError(null);

    // Token更新タイマーをクリア
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Tokenクリア
    AuthService.clearTokens();

    // アカウントデータクリア
    AccountDataService.logout();

    // Issue #972: Ripia関連のlocalStorageを完全クリア
    // clearOrganizationCache()で organizations, userRoles, tenantContext を一括削除
    clearOrganizationCache();
  }, []);

  /**
   * 自動Token更新設定
   */
  const setupTokenRefresh = useCallback(() => {
    // 既存のタイマーをクリア
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // 23時間後にToken更新（24時間有効期限の1時間前）
    refreshTimerRef.current = setTimeout(
      async () => {
        try {
          // Issue #1280 Phase 2: JTI管理統合（refreshTokenSecure使用）
          // refreshTokenSecure()内で以下が自動実行される:
          // - EnhancedJWTService.refreshToken() → 新トークン生成
          // - TokenBlacklistService.revokeToken() → 旧JTIリボーク
          // - saveEncryptedTokenPair() → 新JTI登録 + トークン保存
          const refreshResponse = await AuthService.refreshTokenSecure();

          // 次の更新もスケジュール
          setupTokenRefresh();

          logger.log(
            `✅ Token自動更新完了 (JTI: ${refreshResponse.jti.slice(0, 8)}...)`
          );
        } catch (error) {
          logger.error('Token自動更新失敗:', error);
          // Issue #1280: 型付きエラーでセッション期限切れを明示
          setError({
            type: 'SESSION_EXPIRED',
            message:
              'セッションの更新に失敗しました。再度ログインしてください。',
          });
          await handleLogoutInternal();
        }
      },
      23 * 60 * 60 * 1000
    ); // 23時間
  }, [handleLogoutInternal]);

  // Issue #1567: restoreOrganizationInfo() 削除済み
  // /api/onboarding/status エンドポイントは Issue #1459 で削除
  // Dashboard-First アプローチにより、組織状態の確認はダッシュボードで行う

  /**
   * 認証状態確認
   */
  const checkAuthState = useCallback(async () => {
    // 既に実行中の場合はスキップ（React.StrictModeでの重複実行を防ぐ）
    if (isCheckingRef.current) {
      return;
    }
    isCheckingRef.current = true;

    try {
      setIsInitializing(true);
      setError(null);

      // Issue #485: Preview環境または開発環境（DEV_LOGIN有効）では最優先でPreviewUserをチェック
      const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;
      const mode = import.meta.env.MODE;
      const vercelEnv = import.meta.env.VITE_VERCEL_ENV;
      const isDevMode = mode === 'development' && enableDevLogin === 'true';
      const isPreviewMode = vercelEnv === 'preview';

      if (isDevMode || isPreviewMode) {
        const previewUser = PreviewAuthService.getPreviewUser();
        if (previewUser) {
          // PreviewUserをRipia User型に変換
          const ripiaUser: User = {
            id: previewUser.id,
            email: previewUser.email,
            name: previewUser.name,
            role: previewUser.role,
            createdAt: new Date().toISOString(),
            emailVerified: true,
            isPreview: true,
          };

          // アカウントIDを設定
          AccountDataService.setCurrentAccount(ripiaUser.id, ripiaUser.email);

          setIsAuthenticated(true);
          setUser(ripiaUser);
          setSessionId(previewUser.id);

          setIsInitializing(false);
          isCheckingRef.current = false;
          return; // Preview認証が成功したら、他の認証チェックは不要（Clerkをバイパス）
        }
      }

      // Issue #480: Clerk SDK初期化中はスキップ
      if (!clerkIsLoaded) {
        setIsAuthenticated(false);
        setUser(null);
        setSessionId(null);
        // CRITICAL FIX (Issue #633): 初期化中は isInitializing: true を維持
        // false にすると、ProtectedRoute が即座にリダイレクトして無限ループが発生
        // setIsInitializing(false); ← 削除
        isCheckingRef.current = false;
        return;
      }

      // Issue #480: Clerk認証状態を最優先でチェック
      if (clerkIsLoaded && clerkIsSignedIn && clerkUser) {
        const email = clerkUser.primaryEmailAddress?.emailAddress || '';

        // Issue #1968: ロール判定 - tenantContextを優先、なければメールアドレスから判定
        const tenantContextForRole = AuthService.getCurrentTenantContextV2();
        let userRole: 'owner' | 'manager' | 'staff' = 'owner'; // デフォルトはowner

        if (tenantContextForRole?.tenantRole) {
          // tenantContextにロールがあれば使用
          userRole = tenantContextForRole.tenantRole as
            | 'owner'
            | 'manager'
            | 'staff';
        } else if (
          (isDevMode || isPreviewMode) &&
          email.includes('e2e-staff@')
        ) {
          // E2Eテスト: staffユーザー（dev/preview環境のみ）
          userRole = 'staff';
        } else if (
          (isDevMode || isPreviewMode) &&
          email.includes('e2e-manager@')
        ) {
          // E2Eテスト: managerユーザー（dev/preview環境のみ）
          userRole = 'manager';
        }
        // それ以外（e2e-owner@含む）はownerのまま

        // Clerkユーザー情報をRipia User型に変換
        const ripiaUser: User = {
          id: clerkUser.id,
          email,
          name: clerkUser.fullName || email || clerkUser.username || '',
          role: userRole,
          createdAt: clerkUser.createdAt
            ? new Date(clerkUser.createdAt).toISOString()
            : new Date().toISOString(),
          emailVerified: true, // Clerk認証済み
        };

        // Issue #1149 + #1246: ユーザー切り替え検出とユーザースコープストレージへのマイグレーション
        const storedAccountId = localStorage.getItem(
          'ripia_current_account_id'
        );
        if (!storedAccountId || storedAccountId !== ripiaUser.id) {
          if (storedAccountId) {
            logger.warn(
              `⚠️ [Issue #1149/#1246] User switch detected. Previous: ${storedAccountId}, Current: ${ripiaUser.id}`
            );
          } else {
            logger.info(
              `[Issue #1149/#1246] First login - initializing user-scoped storage`
            );
          }
          // Issue #1246: グローバルキャッシュをクリア（別ユーザーのデータが残っている可能性）
          clearGlobalOrganizationCache();
          // Issue #3643/HAM-1647: user-switch / 初回 init 時に tenant_context cache を
          // invalidate し、前 user の店舗名が sidebar / Dashboard banner に残留するのを防ぐ。
          // 検出済み分岐内のみ＝同一 user 再ログイン時は外側 if に入らず発火しない。
          // 直後の getCurrentTenantContextV2() が null を返し、既存の再fetch 経路で
          // 現 user の店舗名を取得 → tenantContextUpdated event で下流が一括同期する。
          AuthService.clearMultiTenantTokens();
          // canonical 削除後に getCurrentTenantContextV2() の migrate が生存 legacy key を
          // 再昇格させて stale 値を蘇生させる経路を断つ（deterministic 化）。
          for (const legacyKey of LEGACY_TENANT_CONTEXT_KEYS) {
            removeEncryptedToken(legacyKey);
          }
        }

        // WT2引き継ぎ (Issue #1278): Device ID変更検出
        // 別デバイスからのログインを検出して強制サインアウト
        try {
          const currentDeviceId = await getOrCreateDeviceId();
          const deviceIdKey = `ripia:device:${ripiaUser.id}`;
          const storedDeviceId = localStorage.getItem(deviceIdKey);

          if (storedDeviceId && storedDeviceId !== currentDeviceId) {
            logger.warn(
              `[WT2] Device ID mismatch detected. Stored: ${storedDeviceId.substring(0, 8)}..., Current: ${currentDeviceId.substring(0, 8)}...`
            );
            logger.info('[WT2] Forcing re-authentication due to device change');
            // 強制サインアウト
            await clerkAuthData.signOut();
            setIsInitializing(false);
            isCheckingRef.current = false;
            return;
          }

          // Device IDを保存（初回または同一デバイス）
          localStorage.setItem(deviceIdKey, currentDeviceId);
        } catch (deviceIdError) {
          logger.error('[WT2] Device ID check failed:', deviceIdError);
          // Device ID チェック失敗時はアプリ続行（セキュリティは低下するが可用性優先）
        }

        // Issue #1246: レガシーデータがあればユーザースコープへマイグレーション
        // 同じユーザーでも、マイグレーション前のデータが残っている場合に対応
        migrateToUserScopedStorage(ripiaUser.id);

        // WT2引き継ぎ (Issue #1285): localStorageキーマイグレーション
        // KEY_MIGRATION_MAPに定義された旧キー → 新キーへの移行
        if (!isMigrationCompleted(ripiaUser.id)) {
          if (acquireMigrationLock(ripiaUser.id)) {
            try {
              let migratedCount = 0;
              Object.entries(KEY_MIGRATION_MAP).forEach(([oldKey, newKey]) => {
                const value = localStorage.getItem(oldKey);
                if (value) {
                  localStorage.setItem(newKey, value);
                  localStorage.removeItem(oldKey);
                  migratedCount++;
                }
              });
              markMigrationCompleted(ripiaUser.id);
              if (migratedCount > 0) {
                logger.info(
                  `[WT2] Migrated ${migratedCount} localStorage keys for user ${ripiaUser.id.substring(0, 8)}...`
                );
              }
            } catch (migrationError) {
              logger.error('[WT2] Key migration failed:', migrationError);
              // マイグレーション失敗時もアプリは続行（次回ログインで再試行）
            } finally {
              releaseMigrationLock(ripiaUser.id);
            }
          } else {
            logger.debug('[WT2] Migration lock held by another tab, skipping');
          }
        }

        // 現在のアカウントIDを記録
        localStorage.setItem('ripia_current_account_id', ripiaUser.id);

        // アカウントIDを設定（既にログイン済みの場合）
        AccountDataService.setCurrentAccount(ripiaUser.id, ripiaUser.email);

        // Issue #1567: restoreOrganizationInfo() 呼び出し削除
        // /api/onboarding/status エンドポイントは Issue #1459 で削除済み
        // Dashboard-First アプローチにより、組織状態の確認はダッシュボードで行う

        // Issue #2353: tenantContext未設定の場合、バックエンドから自動取得
        let tenantContext = AuthService.getCurrentTenantContextV2();
        if (!tenantContext && clerkIsSignedIn) {
          tenantContext = await AuthService.fetchAndSaveTenantContext(
            ripiaUser.id
          );
        }

        // BUG-5 fix: tenantContext.planType からの setPlan() を削除
        // プランのSSOTは useSubscription (Stripe Subscription) に一元化
        // tenantContext.planType は古いキャッシュの可能性があり、
        // useSubscription の setPlan() と競合してプラン表示不整合を引き起こしていた
        // See: Issue #2366, Issue #1590 (元の追加理由)

        // Issue #1968: Zustand store のユーザー状態を同期
        // Customers.tsx の useUser() は Zustand store を参照するため必須
        // Note: useAuthStore.User と authService.User は型が異なるため変換が必要
        const { setUser: setAuthStoreUser } = useAuthStore.getState();
        setAuthStoreUser({
          id: ripiaUser.id,
          email: ripiaUser.email,
          name: ripiaUser.name,
          role: ripiaUser.role as 'owner' | 'manager' | 'staff',
          store: {
            name: tenantContext?.tenantName || '',
            type: 'salon', // Default salon type
          },
        });
        logger.info('[useAuth] User synced to Zustand store:', ripiaUser.email);

        setIsAuthenticated(true);
        setUser(ripiaUser);
        setSessionId(generateSecureSessionId());

        // PostHog: ユーザー識別（idempotent、毎回呼んでOK）
        identifyUser(ripiaUser.id, {
          email: ripiaUser.email,
          plan: tenantContext?.planType || 'unknown',
          role: userRole,
        });

        // PostHog: sessionStorage未設定 = 新規ログイン（リロードではない）
        if (!sessionStorage.getItem(POSTHOG_LOGIN_TRACKED_KEY)) {
          trackLoginSuccess('clerk');
          sessionStorage.setItem(POSTHOG_LOGIN_TRACKED_KEY, 'true');
        }

        setIsInitializing(false);
        isCheckingRef.current = false;
        return;
      }

      // Issue #1274: Legacy AuthService認証パスを削除
      // Preview認証とClerk認証のみを使用（2パス体制）
      // Clerk認証が成功しなかった場合は未認証状態
      sessionStorage.removeItem(POSTHOG_LOGIN_TRACKED_KEY);
      setIsAuthenticated(false);
      setUser(null);
      setSessionId(null);
    } catch (error) {
      logger.error('認証状態確認エラー:', error);
      setError({ type: 'AUTH_ERROR', message: '認証状態の確認に失敗しました' });
      await handleLogoutInternal();
    } finally {
      // Issue #1758: 認証状態が完全に確定するまで isInitializing を維持
      // - clerkIsLoaded=true (SDK loaded)
      // - clerkIsSignedIn !== undefined (auth state determined)
      // - 認証成功時(clerkIsSignedIn=true)は clerkUser も必要
      const isAuthDetermined =
        clerkIsLoaded &&
        clerkIsSignedIn !== undefined &&
        (clerkIsSignedIn === false ||
          (clerkIsSignedIn === true && clerkUser !== null));

      if (isAuthDetermined) {
        setIsInitializing(false);
      }
      isCheckingRef.current = false;
    }
    // Issue #1274: setupTokenRefreshはLegacy認証パス専用のため削除
    // Issue #1567: restoreOrganizationInfo を依存配列から削除（関数削除済み）
  }, [
    handleLogoutInternal,
    clerkIsLoaded,
    clerkIsSignedIn,
    clerkUser,
    clerkGetToken,
    clerkAuthData,
  ]);

  /**
   * ログアウト
   * Issue #1274 Phase 2: Clerk signOut統合
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      // 1. AuthServiceでログアウト（Backend cleanup）
      await AuthService.logout();

      // 2. Issue #1274 Phase 2: Clerk signOut（セッション終了）
      try {
        await clerkAuthData.signOut();
        logger.log('✅ [Issue #1274] Clerk signOut completed');
      } catch (clerkError) {
        // Clerk signOut失敗時もローカルクリーンアップは継続
        logger.warn(
          '⚠️ Clerk signOut error (continuing with local cleanup):',
          clerkError
        );
      }

      // 3. 内部ログアウト処理（Frontend state cleanup）
      await handleLogoutInternal();

      // ログアウト後のリダイレクトは、認証状態の変更により
      // AppRouterのProtectedRouteが自動的に処理する
    } catch (error) {
      logger.error('ログアウトエラー:', error);

      // エラーが発生してもClerk signOutとローカル状態はクリア
      try {
        await clerkAuthData.signOut();
      } catch (clerkError) {
        logger.warn(
          '[logout] Clerk signOut failed in error recovery:',
          clerkError
        );
      }
      await handleLogoutInternal();
    } finally {
      sessionStorage.removeItem(POSTHOG_LOGIN_TRACKED_KEY);
      trackLogout();
      setLoading(false);
    }
  }, [handleLogoutInternal, clerkAuthData]);

  /**
   * 権限チェック
   */
  const checkPermission = useCallback(
    (action: string): boolean => {
      if (!user || !isAuthenticated) return false;

      // AuthServiceの権限チェックを使用
      return AuthService.hasPermission(action);
    },
    [user, isAuthenticated]
  );

  /**
   * 認証状態更新
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    await checkAuthState();
  }, [checkAuthState]);

  /**
   * エラークリア
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * メール認証付き新規登録
   */
  const registerWithEmailVerification = useCallback(
    async (
      userData: RegisterWithEmailVerificationRequest
    ): Promise<RegisterWithEmailVerificationResponse> => {
      try {
        setLoading(true);
        setError(null);

        const response =
          await AuthService.registerWithEmailVerification(userData);

        return response;
      } catch (error) {
        logger.error('メール認証付き新規登録エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage =
          '新規登録に失敗しました。別のメールアドレスをお試しください。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'REGISTRATION_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * メール認証の実行
   */
  const verifyEmail = useCallback(
    async (request: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
      try {
        setLoading(true);
        setError(null);

        const response = await AuthService.verifyEmail(request);

        if (response.success) {
          // メール認証成功時は認証状態を更新
          await checkAuthState();
        }

        return response;
      } catch (error) {
        logger.error('メール認証エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage =
          '認証に失敗しました。認証コードを確認してください。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'VERIFICATION_FAILED');
      } finally {
        setLoading(false);
      }
    },
    [checkAuthState]
  );

  /**
   * 認証メール再送信
   */
  const resendVerificationEmail = useCallback(
    async (userId: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        await AuthService.resendVerificationEmail({ userId });
      } catch (error) {
        logger.error('認証メール再送信エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage = '認証メールの再送信に失敗しました。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'RESEND_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ====================================
  // MFA関連メソッド
  // ====================================

  /**
   * MFA有効化
   */
  const enableMFA = useCallback(
    async (request: MFAEnableRequest): Promise<MFAEnableResponse> => {
      try {
        setLoading(true);
        setError(null);

        logger.log('🔐 [useAuth] MFA有効化開始:', { userId: request.userId });

        const response = await AuthService.enableMFA(request);

        logger.log('✅ [useAuth] MFA有効化完了');
        return response;
      } catch (error) {
        logger.error('❌ [useAuth] MFA有効化エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage = 'MFAの有効化に失敗しました。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'MFA_ENABLE_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * MFA無効化
   */
  const disableMFA = useCallback(
    async (request: MFADisableRequest): Promise<MFADisableResponse> => {
      try {
        setLoading(true);
        setError(null);

        logger.log('🔐 [useAuth] MFA無効化開始:', { userId: request.userId });

        const response = await AuthService.disableMFA(request);

        logger.log('✅ [useAuth] MFA無効化完了');
        return response;
      } catch (error) {
        logger.error('❌ [useAuth] MFA無効化エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage = 'MFAの無効化に失敗しました。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'MFA_DISABLE_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * MFA認証
   */
  const verifyMFA = useCallback(
    async (request: MFAVerifyRequest): Promise<MFAVerifyResponse> => {
      try {
        setLoading(true);
        setError(null);

        logger.log('🔐 [useAuth] MFA認証開始:', {
          isBackupCode: request.isBackupCode,
        });

        const response = await AuthService.verifyMFA(request);

        if (response.success && response.accessToken && response.user) {
          // アカウントIDを設定
          AccountDataService.setCurrentAccount(
            response.user.id,
            response.user.email
          );

          // 初期データセットアップ（アカウント別）
          await InitialDataService.setupInitialData();

          // MFA認証成功時は認証状態を更新
          setIsAuthenticated(true);
          setUser(response.user);
          logger.log('✅ [useAuth] MFA認証完了 - 認証状態更新済み');
        }

        return response;
      } catch (error) {
        logger.error('❌ [useAuth] MFA認証エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage = 'MFA認証に失敗しました。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'MFA_VERIFICATION_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * MFA状態取得
   */
  const getMFAStatus = useCallback(
    async (userId: string): Promise<MFAStatusResponse> => {
      try {
        setLoading(true);
        setError(null);

        logger.log('🔐 [useAuth] MFA状態取得開始:', { userId });

        const response = await AuthService.getMFAStatus({ userId });

        logger.log('✅ [useAuth] MFA状態取得完了');
        return response;
      } catch (error) {
        logger.error('❌ [useAuth] MFA状態取得エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage = 'MFA状態の取得に失敗しました。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'MFA_STATUS_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * バックアップコード再生成
   */
  const generateNewBackupCodes = useCallback(
    async (
      request: MFAGenerateBackupCodesRequest
    ): Promise<MFAGenerateBackupCodesResponse> => {
      try {
        setLoading(true);
        setError(null);

        logger.log('🔐 [useAuth] バックアップコード再生成開始:', {
          userId: request.userId,
        });

        const response = await AuthService.generateNewBackupCodes(request);

        logger.log('✅ [useAuth] バックアップコード再生成完了');
        return response;
      } catch (error) {
        logger.error('❌ [useAuth] バックアップコード再生成エラー:', error);

        if (error instanceof AuthError) {
          setError({ type: 'AUTH_ERROR', message: error.message });
          throw error;
        }

        const errorMessage = 'バックアップコードの再生成に失敗しました。';
        setError({ type: 'AUTH_ERROR', message: errorMessage });
        throw new AuthError(errorMessage, 'BACKUP_CODES_GENERATION_FAILED');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    isAuthenticated,
    user,
    loading,
    isInitializing,
    error,
    sessionId,
    logout,
    checkPermission,
    refreshAuth,
    clearError,
    registerWithEmailVerification,
    verifyEmail,
    resendVerificationEmail,
    // MFA関連メソッド
    enableMFA,
    disableMFA,
    verifyMFA,
    getMFAStatus,
    generateNewBackupCodes,
    // Preview環境専用メソッド（Issue #485）
    setPreviewUser: PreviewAuthService.setPreviewUser,
    clearPreviewUser: PreviewAuthService.clearPreviewUser,
  };
};

/**
 * 認証フックを使用するためのコンテキストフック
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = (): UseAuth => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext は AuthProvider 内で使用してください');
  }
  return context;
};

/**
 * AuthProviderコンポーネント
 */
export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
